<template>
  <div v-if="loading">Loading...</div>
  <div v-else>
    <div class="card mt-4">
      <div class="card-header">
        <h1 class="mb-0">{{ tour.attributes.tourName }}</h1>
        <router-link :to="{ path: '/calculation-river/tourlistriver' }">
          <b-button v-b-tooltip.hover title="Back to Tour Table" variant="dark">Back to List</b-button>
        </router-link>
      </div>
      <div class="card-body">
        <div class="row">
          <div v-for="(label, key) in details" :key="key" class="col-sm-12 col-md-6 col-lg-3 col">
            <strong>{{ label }}</strong>
            <p>{{ tour.attributes[key] }}</p>
          </div>

          <!-- Cities -->
          <div class="col-sm-12 col-md-6 col-lg-3 col">
            <strong>Cities</strong>
            <p>
              <span v-for="city in tour.attributes.cities.data" :key="city.id">
                {{ city.attributes.city }}<br>
              </span>
            </p>
          </div>

          <!-- Port -->
          <div class="col-sm-12 col-md-6 col-lg-3 col">
            <strong>Port City</strong>
            <p>{{ tour.attributes.port?.data?.attributes?.city || 'N/A' }}</p>
          </div>

          <!-- Timing & Description -->
          <div class="col-sm-12 col-md-6 col-lg-6 col" v-for="(text, key) in texts" :key="key">
            <strong>{{ text }}</strong>
            <textarea class="form-control confined-textarea" v-model="tour.attributes[key]"
              @blur="saveTimingDescription(key)">
            </textarea>
          </div>
          <!-- Comments -->
          <div class="col-sm-12 col-md-6 col-lg-12 col">
            <strong>Comments</strong>
            <div class="position-relative">
              <textarea class="form-control" style="background-color:#d4edda; padding-right: 30px;"
                v-model="tour.attributes.comments" @blur="saveComments">
               </textarea>
              <i class="fas fa-check text-success"
                style="position: absolute; top: 10px; right: 10px; cursor: pointer;"></i>
            </div>
          </div>

          <!-- Remarks -->
          <div class="col-sm-12 col-md-6 col-lg-12 col" v-if="tour.attributes.remarks">
            <strong>Remarks</strong>
            <textarea class="form-control" style="background-color: #f8d7da;" v-model="tour.attributes.remarks"
              @input="updateRemarks">
            </textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Modal -->
    <b-modal ref="serviceModal" id="service-modal" title="Add New Service" @ok="addService">
      <div class="form-group">
        <label for="serviceType">Select Type</label>
        <b-select v-model="selectedType" id="serviceType" class="form-control">
          <option value="" disabled>Select a type</option>
          <option v-for="type in serviceTypes" :key="type"> {{ type }}</option>
        </b-select>
      </div>

      <div class="form-group" v-if="filteredServices.length">
        <label for="newService">Select Service</label>
        <b-select v-model="selectedService" id="newService" class="form-control">
          <option value="" disabled>Select a service</option>
          <option v-for="service in filteredServices" :key="service.id" :value="service.id">
            {{ service.attributes.item }}
          </option>
        </b-select>
      </div>
    </b-modal>

    <!-- Remove Confirmation Modal -->
    <b-modal ref="removeConfirmModal" id="remove-confirm-modal" title="Confirm Removal" @ok="confirmRemoveService">
      <p>Are you sure you want to remove this item from the tour?</p>
    </b-modal>

    <!-- Tables for Different Item Types -->
    <div class="card mt-4">
      <div class="card-header">
        <h6 class="mb-0">Items by Type</h6>
        <b-button variant="info" v-b-tooltip.hover title="Add Services" @click="showModal">+</b-button>
      </div>

      <div class="card-body">
        <!-- Table for Per Person -->
        <div v-if="filteredItems('per person').length">
          <h5>Per Person</h5>
          <table class="table table-striped custom-table">
            <thead class="thead-dark">
              <tr>
                <th>items</th>
                <th v-for="key in filteredItemKeys('per person')" :key="key">{{ key }}</th>
                <th>factor</th>
                <th>margin</th>
                <th>calculated buy(Gross)</th>
                <th>calculated sell(Gross)</th>
                <th>sell price(Gross)</th>
                <th>calculated margin</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems('per person')" :key="item.id">
                <td>{{ item.attributes.item }}</td>
                <td v-for="key in filteredItemKeys('per person')" :key="key">
                  <span v-if="key === 'city'">
                    {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                  </span>
                  <span v-else>
                    <span v-if="['adultPrice_gross', 'adultPrice_net', 'price'].includes(key)">
                      {{ formatPrice(item.attributes[key], city) }}
                    </span>
                    <span v-else>{{ item.attributes[key] }}</span>
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="itemFactors[item.id]"
                    @input="updateItemFactor(item.id)" />
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="margins[item.id]"
                    @input="updateItemFactor(item.id)" />
                </td>
                <td>
                  <span v-if="item.attributes.adultPrice_gross">
                    {{ formatPrice(item.attributes.adultPrice_gross * itemFactors[item.id], city) }}
                  </span>
                </td>
                <td>
                  <span>
                    {{
                      formatPrice(
                        item.attributes.adultPrice_gross * itemFactors[item.id] +
                        item.attributes.adultPrice_gross * itemFactors[item.id] * (margins[item.id] / 100),
                        city
                      )
                    }}
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="sellPrices[item.id]"
                    @input="updateItemFactor(item.id, { sellPrice: sellPrices[item.id], itemFactor: itemFactors[item.id], margin: margins[item.id] })" />
                </td>
                <td>
                  <span>
                    {{ formatMargin(calculateMargin(item.attributes.adultPrice_gross * itemFactors[item.id],
                      sellPrices[item.id])) }}
                  </span>
                </td>
                <td>
                  <b-button size="sm" class="remove-btn" v-b-tooltip.hover title="Remove Per Person Service From Tour"
                    @click="showRemoveConfirm(item.id)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Table for Bulk -->
        <div v-if="filteredItems('bulk').length">
          <h5>Bulk</h5>
          <table class="table table-striped custom-table">
            <thead class="thead-dark">
              <tr>
                <th>items</th>
                <th v-for="key in filteredItemKeys('bulk')" :key="key">{{ key }}</th>
                <th>factor</th>
                <th>margin</th>
                <th>calculated buy(Gross)</th>
                <th>calculated sell(Gross)</th>
                <th>sell price(Gross)</th>
                <th>calculated margin</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems('bulk')" :key="item.id">
                <td>{{ item.attributes.item }}</td>
                <td v-for="key in filteredItemKeys('bulk')" :key="key">
                  <span v-if="key === 'city'">
                    {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                  </span>
                  <span v-else>
                    <span v-if="['adultPrice_gross', 'adultPrice_net', 'price'].includes(key)">
                      {{ formatPrice(item.attributes[key], city) }}
                    </span>
                    <span v-else>
                      {{ item.attributes[key] }}
                    </span>
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="itemFactors[item.id]"
                    @input="updateItemFactor(item.id, itemFactors[item.id])" />
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="margins[item.id]"
                    @input="updateItemFactor(item.id, margins[item.id])" />
                </td>
                <td>
                  <span v-if="item.attributes.price">
                    {{ formatPrice(item.attributes.price * itemFactors[item.id], city) }}
                  </span>
                </td>
                <td>
                  <span>
                    {{
                      formatPrice(
                        item.attributes.price * itemFactors[item.id] +
                        item.attributes.price * itemFactors[item.id] * (margins[item.id] / 100),
                        city
                      )
                    }}
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="sellPrices[item.id]"
                    @input="updateItemFactor(item.id, { sellPrice: sellPrices[item.id], itemFactor: itemFactors[item.id], margin: margins[item.id] })" />
                </td>
                <td>
                  <span>
                    {{ formatMargin(calculateMargin(item.attributes.price * itemFactors[item.id], sellPrices[item.id]))
                    }}
                  </span>
                </td>
                <td>
                  <b-button size="sm" class="remove-btn" v-b-tooltip.hover title="Remove Bulk Service From Tour"
                    @click="showRemoveConfirm(item.id)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Table for Hourly -->
        <div v-if="filteredItems('hourly').length">
          <h5>Hourly</h5>
          <table class="table table-striped custom-table">
            <thead class="thead-dark">
              <tr>
                <th>items</th>
                <th v-for="key in filteredItemKeys('hourly')" :key="key">{{ key }}</th>
                <th>factor</th>
                <th>margin</th>
                <th>calculated buy(Gross)</th>
                <th>calculated sell(Gross)</th>
                <th>sell price(Gross)</th>
                <th>calculated margin</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems('hourly')" :key="item.id">
                <td>{{ item.attributes.item }}</td>
                <td v-for="key in filteredItemKeys('hourly')" :key="key">
                  <span v-if="key === 'city'">
                    {{ item.attributes.city?.data?.attributes?.city || 'N/A' }}
                  </span>
                  <span v-else>
                    <span v-if="['adultPrice_gross', 'adultPrice_net', 'price'].includes(key)">
                      {{ formatPrice(item.attributes[key], city) }}
                    </span>
                    <span v-else>{{ item.attributes[key] }}</span>
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="itemFactors[item.id]"
                    @input="updateItemFactor(item.id)" />
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="margins[item.id]"
                    @input="updateItemFactor(item.id)" />
                </td>
                <td>
                  <span v-if="item.attributes.price">
                    {{ formatPrice(item.attributes.price * itemFactors[item.id], city) }}
                  </span>
                </td>
                <td>
                  <span>
                    {{
                      formatPrice(
                        item.attributes.price * itemFactors[item.id] +
                        item.attributes.price * itemFactors[item.id] * (margins[item.id] / 100),
                        city
                      )
                    }}
                  </span>
                </td>
                <td>
                  <b-input style="  width: 50%;" type="number" step="0.01" v-model="sellPrices[item.id]"
                    @input="updateItemFactor(item.id, { sellPrice: sellPrices[item.id], itemFactor: itemFactors[item.id], margin: margins[item.id] })" />
                </td>
                <td>
                  <span>
                    {{ formatMargin(calculateMargin(item.attributes.price * itemFactors[item.id], sellPrices[item.id]))
                    }}
                  </span>
                </td>
                <td>
                  <b-button size="sm" class="remove-btn" v-b-tooltip.hover title="Remove Hourly Service From Tour"
                    @click="showRemoveConfirm(item.id)">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- No Items Available Message -->
        <div
          v-if="!filteredItems('per person').length && !filteredItems('bulk').length && !filteredItems('hourly').length">
          <p>No items available for this tour.</p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { formatPrice } from '../Helper/PriceDisplay.js';

export default {
  data() {
    return {
      tour: {},
      loading: true,
      availableServices: [],
      selectedService: null,
      selectedType: null,
      serviceTypes: [],
      filteredServices: [],
      details: {
        tourName: 'Tour Name',
        tourCode: 'Tour Code',
        duration: 'Duration',
        paxMin: 'Min Pax',
        paxMax: 'Max Pax',
        cruiseLine: 'Cruise Line'
      },
      texts: {
        timing: 'Timings',
        description: 'Description'
      },
      itemKeys: [
        'tax',
        'rate',
        'category',
        'remarks',
        'supplier',
        'adultPrice_gross',
        'adultPrice_net',
        'price',
      ],
      serviceToRemove: null,
      itemFactors: {},
      margins: {},
      sellPrices: {},
      remarks: ''
    };
  },
  async created() {
    const tourId = this.$route.params.id;

    // Fetch tour data
    const tourResponse = await this.$xhr.get(`/api/tourrivers/${tourId}?populate[item][populate]=*&populate[cities][populate]=*&populate[port][populate]=*`);
    this.tour = tourResponse.data.data;
    this.itemFactors = this.tour.attributes.itemFactor || {};
    this.margins = this.tour.attributes.margin || {};
    this.sellPrices = this.tour.attributes.sellPrice || {};

    const tourYear = this.tour.attributes.year;
    const tourCities = this.tour.attributes.cities?.data?.map(city => city.attributes?.city) || [];

    console.log('[TourTableRiver] Tour Year:', tourYear);
    console.log('[TourTableRiver] Tour Cities:', tourCities);

    // Fetch all matching service-rivers using pagination loops
    let allServices = [];
    let pageNum = 1;
    let pageSize = 500;
    let totalFound = 0;
    let hasMore = true;

    const queryParams = new URLSearchParams();
    queryParams.append('populate[0]', 'cities');
    queryParams.append('pagination[pageSize]', pageSize);
    queryParams.append('filters[year][$eq]', tourYear);

    if (tourCities.length > 0) {
      tourCities.forEach((city) => {
        queryParams.append('filters[cities][city][$in]', city);
      });
    }

    console.log('[TourTableRiver] Starting paginated fetch for matches...');
    console.log('[TourTableRiver] URL Params:', queryParams.toString());

    try {
      while (hasMore) {
        queryParams.set('pagination[page]', pageNum);
        const response = await this.$xhr.get(`/api/servicerivers?${queryParams.toString()}`);
        const data = response.data.data;
        const meta = response.data.meta;

        allServices = allServices.concat(data);
        totalFound = meta.pagination.total;

        console.log(`[TourTableRiver] Loaded page ${pageNum}, items: ${allServices.length}/${totalFound}`);

        if (allServices.length >= totalFound || data.length < pageSize) {
          hasMore = false;
        } else {
          pageNum++;
        }
      }

      // If still empty and we used city filter, try fallback without city filter
      if (allServices.length === 0 && tourCities.length > 0) {
        console.warn('[TourTableRiver] No services found with city filter. Trying fallback (Year only)...');
        const fallbackParams = new URLSearchParams();
        fallbackParams.append('populate[0]', 'cities');
        fallbackParams.append('filters[year][$eq]', tourYear);
        fallbackParams.append('pagination[pageSize]', '100');
        const fallbackRes = await this.$xhr.get(`/api/servicerivers?${fallbackParams.toString()}`);
        allServices = fallbackRes.data.data;
        console.log('[TourTableRiver] Fallback found items:', allServices.length);
      }
    } catch (error) {
      console.error('[TourTableRiver] Error fetching services:', error);
      if (error.response) {
        console.error('[TourTableRiver] Error data:', error.response.data);
      }
      this.$bvToast.toast('Failed to load available services.', { variant: 'danger' });
    }

    this.availableServices = allServices;
    console.log('[TourTableRiver] Final available services count:', this.availableServices.length);

    // Populate service types
    this.serviceTypes = [...new Set(this.availableServices.map(service => service.attributes.type))].filter(Boolean).sort();
    console.log('[TourTableRiver] Service Types available:', this.serviceTypes);

    this.loading = false;
  },
  watch: {
    selectedType(newType) {
      this.filteredServices = this.availableServices
        .filter(service => service.attributes.type === newType)
        .sort((a, b) => a.attributes.item.localeCompare(b.attributes.item)); // Sorting alphabetically
      this.selectedService = null;
    }
  },
  computed: {
    city() {
      const cities = this.tour?.attributes?.cities?.data;
      if (!cities || cities.length === 0) return null;
      const prague = cities.find(c => c.attributes.city === 'Prague');
      return prague ? 'Prague' : cities[0].attributes.city;
    }
  },
  methods: {
    formatPrice,
    formatMargin(value) {
      return value !== null ? `${value.toFixed(2)}%` : 'N/A';
    },
    calculateMargin(buyPrice, sellPrice) {
      if (!buyPrice || !sellPrice || buyPrice === 0) return null;
      return ((sellPrice - buyPrice) / buyPrice) * 100;
    },
    filteredItemKeys(type) {
      if (type === 'per person') {
        return this.itemKeys.filter(key => !['price'].includes(key));
      } else {
        return this.itemKeys.filter(key => !['adultPrice_net', 'adultPrice_gross'].includes(key));
      }
    },
    async addService() {
      console.log('[TourTableRiver] Trying to add service ID:', this.selectedService);
      if (this.selectedService) {
        try {
          const newService = await this.$xhr.get(`/api/servicerivers/${this.selectedService}?populate=*`).then(res => res.data.data);
          console.log('[TourTableRiver] Fetched new service:', newService.id, newService.attributes.item);

          // Initialize items if missing
          if (!this.tour.attributes.item) {
            this.tour.attributes.item = { data: [] };
          }
          if (!this.tour.attributes.item.data) {
            this.tour.attributes.item.data = [];
          }

          // Add the new service to the tour's local items
          this.tour.attributes.item.data.push(newService);

          // Set default itemFactor to 1 if not already set
          if (!newService.attributes.itemFactor && !this.itemFactors[newService.id]) {
            this.itemFactors[newService.id] = 1;
          } else if (newService.attributes.itemFactor) {
            this.itemFactors[newService.id] = newService.attributes.itemFactor;
          }

          // Set default margin to 15 if not already set
          if (!this.margins[newService.id]) {
            this.margins[newService.id] = 15;
          }

          const payload = {
            data: {
              item: this.tour.attributes.item.data.map(service => ({ id: service.id })),
              itemFactor: this.itemFactors,
              margin: this.margins,
              sellPrice: this.sellPrices,
            }
          };

          console.log('[TourTableRiver] Sending update payload:', payload);
          await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, payload);

          this.selectedService = null;
          this.selectedType = null;

          this.$bvToast.toast('Item added successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } catch (error) {
          console.error('[TourTableRiver] Failed to add service:', error);
          this.$bvToast.toast('Failed to add service. Check console for details.', { variant: 'danger' });
        }
      }
    },
    showRemoveConfirm(serviceId) {
      this.serviceToRemove = serviceId;
      this.$refs.removeConfirmModal.show();
    },
    async confirmRemoveService() {
      this.tour.attributes.item.data = this.tour.attributes.item.data.filter(item => item.id !== this.serviceToRemove);

      // Delete associated data for the removed service
      delete this.sellPrices[this.serviceToRemove];
      delete this.itemFactors[this.serviceToRemove];
      delete this.margins[this.serviceToRemove];

      // Update the overview with calculated buy and factor
      const updatedOverview = this.tour.attributes.item.data.map(item => ({
        name: item.attributes.item,
        type: item.attributes.type,
        calculatedBuy: item.attributes.price * this.itemFactors[item.id] || 0,
        factor: this.itemFactors[item.id] || 0,
        margin: this.margins[item.id] || 0,
        sellPrice: this.sellPrices[item.id] || 0
      }));

      await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, {
        data: {
          item: this.tour.attributes.item.data.map(service => ({ id: service.id })),
          itemFactor: this.itemFactors,
          margin: this.margins,
          sellPrice: this.sellPrices,
          overview: updatedOverview
        }
      });

      this.$bvToast.toast('Item removed successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 5000
      });
      this.serviceToRemove = null;
    },
    async updateItemFactor(itemId, params = {}) {
      const {
        sellPrice = this.sellPrices[itemId],
        itemFactor = this.itemFactors[itemId],
        margin = this.margins[itemId]
      } = params;

      // Update values in local state
      this.sellPrices[itemId] = sellPrice;
      this.itemFactors[itemId] = itemFactor;
      this.margins[itemId] = margin;

      // Generate updated overview
      const updatedOverview = this.tour.attributes.item.data.map(item => {
        let calculatedBuy = 0;

        if (item.attributes.type === "per person") {
          calculatedBuy = (item.attributes.adultPrice_gross || 0) * (this.itemFactors[item.id] || 1);
        } else {
          calculatedBuy = (item.attributes.price || 0) * (this.itemFactors[item.id] || 1);
        }

        const calculatedSell = calculatedBuy + (calculatedBuy * (this.margins[item.id] || 0) / 100);
        const calculatedMargin = this.calculateMargin(calculatedBuy, this.sellPrices[item.id]);

        return {
          name: item.attributes.item,
          type: item.attributes.type,
          calculatedBuy,
          factor: this.itemFactors[item.id] || 0,
          margin: this.margins[item.id] || 0,
          sellPrice: this.sellPrices[item.id] || calculatedSell,
          calculatedMargin
        };
      });

      const updatedData = {
        data: {
          itemFactor: this.itemFactors,
          margin: this.margins,
          sellPrice: this.sellPrices,
          overview: updatedOverview
        }
      };

      try {
        const response = await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, updatedData);
        if (response.status === 200) {
          console.log("Tour updated successfully");
        }
      } catch (error) {
        console.error("Error updating tour", error);
        this.$bvToast.toast('Failed to update the tour.', { variant: 'danger' });
      }
    },
    async updateRemarks() {
      // Update the remarks field in the backend
      await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, {
        data: { remarks: this.tour.attributes.remarks }
      });
      this.$bvToast.toast('Remarks updated successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 5000
      });
    },
    async saveComments() {
      try {
        await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, {
          data: {
            comments: this.tour.attributes.comments,
          },
        });
        this.$bvToast.toast('Comments saved successfully.', { variant: 'success' });
      } catch (error) {
        this.$bvToast.toast('Failed to save comments.', { variant: 'danger' });
      }
    },
    async saveTimingDescription(key) {
      try {
        await this.$xhr.put(`/api/tourrivers/${this.tour.id}`, {
          data: {
            [key]: this.tour.attributes[key],
          },
        });
        this.$bvToast.toast(`${key} saved successfully.`, { variant: 'success' });
      } catch (error) {
        this.$bvToast.toast(`Failed to save ${key}.`, { variant: 'danger' });
      }
    },
    filteredItems(type) {
      return this.tour.attributes?.item?.data?.filter(item => item.attributes.type === type) || [];
    },
    showModal() {
      this.$refs.serviceModal.show();
    }
  }
};
</script>
<style scoped>
.remove-btn {
  background-color: red;
  color: white;
  margin-left: 1rem;
}

.confined-textarea {
  height: 20rem;
  text-align: justify
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h1,
.card-header h6 {
  flex: 1;
  text-align: center;
}
</style>